import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CopyIcon, XIcon } from "lucide-react";
import { planRegistry } from "@/data/meal-plan";
import type { DayOfWeek, WeekPlan } from "@/types/meal-plan-state";
import { DAYS_OF_WEEK } from "@/types/meal-plan-state";
import { useWeekPlan } from "@/context/week-plan-context";
import { fetchWeekSnapshot, SELF_SCOPE } from "@/lib/api";
import { applyCopyMealNotes, planCopyMealNotes, type CopyCandidate, type CopyMealName } from "@/lib/copy-meal-notes";
import { flushPendingSaves } from "@/lib/pending-saves";
import { migrateStoredWeek } from "@/lib/plan-switch";
import { focusRing } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { ComprasToast, useComprasToast } from "@/components/ui/compras-toast";

const DAY_NAMES: Record<DayOfWeek, string> = {
  mon: "Lunes",
  tue: "Martes",
  wed: "Miércoles",
  thu: "Jueves",
  fri: "Viernes",
  sat: "Sábado",
  sun: "Domingo",
};

const MEAL_LABELS: Record<CopyMealName, string> = { lunch: "Almuerzo", dinner: "Cena" };

const UNDO_MS = 5000;

/**
 * How long the up-front read of my week stands in for a fresh one when the
 * dialog opens. In friend mode my week only changes from another device, so
 * a recent read is good enough; an older one is read again.
 */
const PRECHECK_FRESH_MS = 60_000;

const candidateKey = (c: { day: DayOfWeek; meal: CopyMealName }) => `${c.day}:${c.meal}`;

/** "martes", "martes y viernes", "lunes, martes y viernes". */
function joinDays(days: DayOfWeek[]): string {
  const names = days.map((d) => DAY_NAMES[d].toLowerCase());
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`;
}

function comidas(n: number): string {
  return n === 1 ? "1 comida" : `${n} comidas`;
}

/** "9 comidas copiadas." plus, when some only got the note, "2 solo con la nota: revisa martes y viernes." */
export function copyResultMessage(copied: number, noteOnly: { day: DayOfWeek }[]): string {
  const head = copied === 1 ? "1 comida copiada." : `${copied} comidas copiadas.`;
  if (noteOnly.length === 0) return head;
  const days = DAYS_OF_WEEK.map((d) => d.id).filter((d) => noteOnly.some((n) => n.day === d));
  return `${head} ${noteOnly.length} solo con la nota: revisa ${joinDays(days)}.`;
}

/** The planner's own copy of a week (null: not planned), as stored on the server. */
async function fetchPlannerWeek(weekId: string): Promise<WeekPlan | null> {
  const snapshot = await fetchWeekSnapshot(SELF_SCOPE, weekId);
  return snapshot.plan ? migrateStoredWeek(snapshot.plan) : null;
}

type SourceState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; plan: WeekPlan | null };

/**
 * Friend mode, top of Comidas: "Copiar mis comidas de esta semana", or a
 * muted line while the planner's own week has no lunch or dinner notes.
 * Opening it reuses the up-front read of the planner's week when it is
 * recent (PRECHECK_FRESH_MS), else saves what's pending and reads it again.
 * Copying is one `SET_WEEK_PLAN` (one save), undoable from the toast for
 * 5 s while the week is still the copy's result. Mount it with
 * `key={weekId}`: the dialog, the check and the undo belong to one week.
 */
export function CopyMealNotesSection() {
  const { state, dispatch, owner } = useWeekPlan();
  const weekId = state.currentWeekId;
  const [precheck, setPrecheck] = useState<{ weekId: string; source: SourceState; at: number } | null>(null);
  const [dialog, setDialog] = useState<SourceState | null>(null);
  const openTokenRef = useRef(0);
  const { toast, show: showToast, dismiss: dismissToast } = useComprasToast();

  // Undo: the week before the copy, valid while the shown week is the copy's result.
  const undoRef = useRef<{ before: WeekPlan; result: WeekPlan; message: string } | null>(null);
  const weekPlanRef = useRef(state.weekPlan);
  weekPlanRef.current = state.weekPlan;

  // Know up front whether there's anything to copy (button or muted line).
  useEffect(() => {
    if (!weekId) return;
    let cancelled = false;
    setPrecheck({ weekId, source: { status: "loading" }, at: Date.now() });
    // My own edits may still be debounced (I just came from my week).
    flushPendingSaves()
      .catch((error: unknown) => console.error("Failed to save before reading my week", error))
      .then(() => fetchPlannerWeek(weekId))
      .then(
        (plan) => {
          if (!cancelled) setPrecheck({ weekId, source: { status: "ready", plan }, at: Date.now() });
        },
        (error: unknown) => {
          console.error("Failed to load my week for copying", error);
          if (!cancelled) setPrecheck({ weekId, source: { status: "error" }, at: Date.now() });
        },
      );
    return () => {
      cancelled = true;
    };
  }, [weekId]);

  const precheckRef = useRef(precheck);
  precheckRef.current = precheck;

  const openDialog = useCallback(async (reuse = true) => {
    const token = ++openTokenRef.current;
    const checked = precheckRef.current;
    if (
      reuse &&
      checked?.weekId === weekId &&
      checked.source.status === "ready" &&
      Date.now() - checked.at < PRECHECK_FRESH_MS
    ) {
      setDialog(checked.source);
      return;
    }
    setDialog({ status: "loading" });
    try {
      await flushPendingSaves();
    } catch (error) {
      // Still copy from what the server has; the failed save shows its own error.
      console.error("Failed to save before reading my week", error);
    }
    try {
      const plan = await fetchPlannerWeek(weekId);
      if (token !== openTokenRef.current) return;
      setDialog({ status: "ready", plan });
      setPrecheck({ weekId, source: { status: "ready", plan }, at: Date.now() });
    } catch (error) {
      console.error("Failed to load my week for copying", error);
      if (token === openTokenRef.current) setDialog({ status: "error" });
    }
  }, [weekId]);

  const closeDialog = useCallback(() => {
    openTokenRef.current += 1;
    setDialog(null);
  }, []);

  // The week changed after the copy (another edit, a merge): undo is no longer offered.
  useEffect(() => {
    const undo = undoRef.current;
    if (!undo || state.weekPlan === undo.result) return;
    undoRef.current = null;
    showToast({ message: undo.message, tone: "success" });
  }, [state.weekPlan, showToast]);

  const undoCopy = useCallback(() => {
    const undo = undoRef.current;
    undoRef.current = null;
    if (!undo || weekPlanRef.current !== undo.result) return;
    dispatch({ type: "SET_WEEK_PLAN", plan: undo.before, origin: "edit" });
  }, [dispatch]);

  const copy = useCallback(
    (picks: CopyCandidate[]) => {
      const before = weekPlanRef.current;
      const { plan, noteOnly, copied } = applyCopyMealNotes(before, picks);
      closeDialog();
      if (copied === 0) return;
      dispatch({ type: "SET_WEEK_PLAN", plan, origin: "edit" });
      const message = copyResultMessage(copied, noteOnly);
      undoRef.current = { before, result: plan, message };
      showToast({ message, tone: "success", actionLabel: "Deshacer", onAction: undoCopy, durationMs: UNDO_MS });
    },
    [closeDialog, dispatch, showToast, undoCopy],
  );

  const current = precheck?.weekId === weekId ? precheck.source : null;
  const hasNotes =
    current?.status === "ready" && current.plan !== null && planCopyMealNotes(current.plan, state.weekPlan).length > 0;

  return (
    <>
      {current?.status === "ready" && !hasNotes ? (
        <p className="px-1 text-sm text-muted-foreground">
          Cuando planifiques tus almuerzos y cenas, podrás copiarlos aquí.
        </p>
      ) : current ? (
        // Shown while checking (and if the check failed): opening re-reads my week anyway.
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full"
          disabled={!state.weekLoaded || current.status === "loading"}
          onClick={() => void openDialog()}
        >
          <CopyIcon />
          Copiar mis comidas de esta semana
        </Button>
      ) : null}

      {dialog && (
        <CopyMealNotesDialog
          ownerName={owner.username}
          planName={state.planId ? planRegistry[state.planId].name : null}
          source={dialog}
          target={state.weekPlan}
          onRetry={() => void openDialog(false)}
          onCopy={copy}
          onClose={closeDialog}
        />
      )}

      <ComprasToast toast={toast} onDismiss={dismissToast} />
    </>
  );
}

// ── Dialog (design screen 6) ──

interface CopyMealNotesDialogProps {
  ownerName: string;
  /** The owner's plan name, for "Se ajustan a las porciones de …". */
  planName: string | null;
  source: SourceState;
  /** The owner's week as shown now: candidates follow it live. */
  target: WeekPlan;
  onRetry: () => void;
  onCopy: (picks: CopyCandidate[]) => void;
  onClose: () => void;
}

export function CopyMealNotesDialog({
  ownerName,
  planName,
  source,
  target,
  onRetry,
  onCopy,
  onClose,
}: CopyMealNotesDialogProps) {
  const sourcePlan = source.status === "ready" ? source.plan : null;
  const candidates = useMemo(
    () => (sourcePlan ? planCopyMealNotes(sourcePlan, target) : []),
    [sourcePlan, target],
  );

  // Defaults are taken once, when my week arrives: only empty meals are checked.
  const [checked, setChecked] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (source.status !== "ready" || checked !== null) return;
    setChecked(new Set(candidates.filter((c) => c.defaultChecked).map(candidateKey)));
  }, [source.status, candidates, checked]);

  const picks = candidates.filter((c) => c.targetMealIndex !== null && checked?.has(candidateKey(c)));

  function toggle(key: string) {
    setChecked((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          "flex flex-col gap-0 p-0 max-h-[90dvh]",
          "fixed inset-x-0 bottom-0 top-auto w-full max-w-none translate-x-0 translate-y-0 rounded-t-2xl border-t border-x border-b-0",
          "sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border-b",
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-2 border-b px-5 py-4">
          <div className="min-w-0">
            <DialogTitle className="text-base font-semibold break-words">Copiar tus comidas a {ownerName}</DialogTitle>
            <DialogDescription className="mt-0.5">
              Se ajustan a las porciones de {planName ?? "su plan"}.
            </DialogDescription>
          </div>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Cerrar"
              className={cn(
                "-mr-2 -mt-1 grid size-10 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted",
                focusRing,
              )}
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {source.status === "loading" && (
            <p className="py-6 text-center text-sm text-muted-foreground" role="status">
              Cargando tus comidas…
            </p>
          )}
          {source.status === "error" && (
            <div className="space-y-3 py-6 text-center" role="alert">
              <p className="text-sm text-muted-foreground">No se pudieron cargar tus comidas.</p>
              <Button type="button" variant="outline" onClick={onRetry}>
                Reintentar
              </Button>
            </div>
          )}
          {source.status === "ready" && candidates.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Esta semana no tienes notas de almuerzo ni de cena.
            </p>
          )}
          {source.status === "ready" && candidates.length > 0 && (
            <div className="divide-y">
              {DAYS_OF_WEEK.map(({ id: day }) => {
                const dayCandidates = candidates.filter((c) => c.day === day);
                return (
                  <section key={day} className="py-2.5" aria-label={DAY_NAMES[day]}>
                    <h3 className="text-sm font-semibold">{DAY_NAMES[day]}</h3>
                    {dayCandidates.length === 0 ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">Sin almuerzo ni cena en tu semana</p>
                    ) : (
                      <ul className="mt-0.5">
                        {dayCandidates.map((c) => (
                          <CandidateRow
                            key={candidateKey(c)}
                            candidate={c}
                            ownerName={ownerName}
                            targetNote={
                              c.targetMealIndex === null ? "" : (target[day].mealNotes?.[c.targetMealIndex] ?? "").trim()
                            }
                            checked={checked?.has(candidateKey(c)) ?? false}
                            onToggle={() => toggle(candidateKey(c))}
                          />
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>

        <footer className="flex shrink-0 items-center gap-3 border-t px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Button type="button" variant="outline" size="lg" className="h-11 flex-1" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            size="lg"
            className="h-11 flex-[1.4]"
            disabled={picks.length === 0}
            onClick={() => onCopy(picks)}
          >
            {source.status !== "ready" ? "Copiar" : picks.length > 0 ? `Copiar ${comidas(picks.length)}` : "Nada que copiar"}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function CandidateRow({
  candidate,
  ownerName,
  targetNote,
  checked,
  onToggle,
}: {
  candidate: CopyCandidate;
  ownerName: string;
  /** The owner's current note on the target meal ("" if none). */
  targetNote: string;
  checked: boolean;
  onToggle: () => void;
}) {
  const label = MEAL_LABELS[candidate.meal];
  if (candidate.targetMealIndex === null) {
    return (
      <li className="py-1.5 text-xs text-muted-foreground">
        {ownerName} no tiene {label.toLowerCase()} este día
      </li>
    );
  }
  const same = targetNote !== "" && targetNote.toLowerCase() === candidate.note.toLowerCase();
  const hint = same
    ? "Ya tiene esta comida"
    : candidate.replaces === null
      ? null
      : candidate.replaces === targetNote
        ? `Reemplaza: "${candidate.replaces}"`
        : `Reemplaza: ${candidate.replaces}`;
  return (
    <li>
      <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-md py-2 -mx-1 px-1 hover:bg-muted/40">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-0.5 size-5 shrink-0 accent-primary"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-sm break-words">
            <span className="font-medium">{label}</span>
            <span className="text-muted-foreground"> · </span>
            {candidate.note}
          </span>
          {hint && <span className="mt-0.5 block text-xs text-muted-foreground break-words">{hint}</span>}
        </span>
      </label>
    </li>
  );
}
